import React from 'react';
import type { SessionStatus } from './types';
import { splitImprovementSection } from './history';
import { Markdown } from './markdown';

export function saveMarkdown(markdown: string, name: string): void {
  const url = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Feedback with the "Top 3 improvements" section lifted to the top. */
export function FeedbackBody({ markdown }: { markdown: string }) {
  const { plan, rest } = React.useMemo(() => splitImprovementSection(markdown), [markdown]);
  return (
    <>
      {plan && (
        <section aria-label="Improvement plan" className="improvement-plan">
          <Markdown className="md--debrief" source={plan} />
        </section>
      )}
      {rest && <Markdown className="md--debrief" source={rest} />}
    </>
  );
}

export function CopyDownload({ filename, markdown }: { filename: string; markdown: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <>
      <button
        onClick={() => {
          void navigator.clipboard
            .writeText(markdown)
            .then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1_800);
            })
            .catch(() => setCopied(false));
        }}
        type="button"
      >
        {copied ? 'Copied' : 'Copy markdown'}
      </button>
      <button onClick={() => saveMarkdown(markdown, filename)} type="button">Download .md</button>
    </>
  );
}

const STATUS_LABEL: Record<SessionStatus, string> = {
  live: 'Live',
  debriefing: 'Generating feedback',
  done: 'Ready',
  failed: 'Failed',
};

export function StatusChip({ status }: { status: SessionStatus }) {
  return <span className={`status-chip status-chip--${status}`}>{STATUS_LABEL[status]}</span>;
}

/** Collapsed by default. */
export function TranscriptToggle({ children, count }: { children: React.ReactNode; count: number }) {
  const [shown, setShown] = React.useState(false);
  return (
    <section className="full-transcript">
      <header>
        <h2>Conversation</h2>
        <button className="transcript-toggle transcript-toggle--light" onClick={() => setShown((value) => !value)} type="button">
          {shown ? 'Hide transcript' : `Show transcript (${count})`}
        </button>
      </header>
      {shown && children}
    </section>
  );
}
